<div class="flex flex-column gap-4">
  <p-toolbar>
    <div class="p-toolbar-group-start">
      <h1 class="text-3xl font-bold m-0">Customers</h1>
    </div>

    <div class="p-toolbar-group-center flex-grow-1 mx-2">
      <div class="p-inputgroup flex-1">
        <span class="p-inputgroup-addon">
          <i class="pi pi-search"></i>
        </span>
        <input type="text" pInputText [(ngModel)]="searchTerm" (input)="filterCustomers()"
          placeholder="Search customers..." class="w-full"/>
        <span *ngIf="searchTerm" class="p-inputgroup-addon cursor-pointer" (click)="searchTerm=''; filterCustomers()">
          <i class="pi pi-times"></i>
        </span>
      </div>
    </div>

    <div class="p-toolbar-group-end flex gap-2">
      <p-dropdown [options]="statusOptions" [(ngModel)]="selectedStatus" (onChange)="filterCustomers()"
        placeholder="Status" [showClear]="true"></p-dropdown>

      <p-dropdown [options]="customerTypeOptions" [(ngModel)]="selectedCustomerType" (onChange)="filterCustomers()"
        placeholder="Customer Type" [showClear]="true"></p-dropdown>

      <p-button *ngIf="hasAccess" icon="pi pi-plus" label="Add Customer" styleClass="p-button-outlined"
        (onClick)="addCustomer()"></p-button>
    </div>
  </p-toolbar>

  <!-- Grid View -->
  <div class="grid" style="max-height: 80vh; overflow-y: auto">
    <div *ngFor="let customer of filteredCustomers" class="col-4" style="align-self: stretch;" [id]="'customer-' + customer.customer_id">
      <p-card>
        <!-- Card Header -->
        <ng-template pTemplate="header">
          <div class="flex justify-content-between align-items-start p-3 pb-2">
            <div class="flex-1">
              <h3 class="text-xl font-semibold m-0 mb-1">{{customer.name}}</h3>
              <div class="text-sm text-500 font-medium">{{getCustomerType(customer)}}</div>
            </div>
            <div class="flex align-items-center gap-1 flex-shrink-0">
              <p-tag [severity]="customer.status === 'Active' ? 'success' : 'warning'"
                [value]="customer.status"></p-tag>
              <p-menu #menu [popup]="true" [model]="menuItems" [appendTo]="'body'"
                [styleClass]="'custom-menu'"></p-menu>
              <button pButton type="button" icon="pi pi-ellipsis-v" class="p-button-rounded p-button-text"
                (click)="menu.toggle($event); setSelectedCustomer(customer)" 
                [attr.aria-label]="'Actions for ' + customer.name"></button>
            </div>
          </div>
        </ng-template>

        <!-- Contact Details - Only show when single address -->
        <div *ngIf="!hasMultipleAddresses(customer)" class="mb-3 mt-3">
          <!-- Check if single address and single contact for two-column layout -->
          <div *ngIf="getPrimaryAddress(customer) && getContactCount(getPrimaryAddress(customer)!) === 1" class="flex gap-6">
            <!-- Left Column: Address Information and Notes -->
            <div class="flex-1 flex flex-column gap-3">
              <div class="flex align-items-start gap-3">
                <i class="pi pi-map-marker text-500 mt-1"></i>
                <div class="flex flex-column gap-1">
                  <span class="font-medium">{{getCustomerAddress(customer)}}</span>
                  <span class="text-sm text-500">{{getCustomerCity(customer)}}, {{getCustomerState(customer)}} {{getCustomerZipcode(customer)}}</span>
                </div>
              </div>
              <div *ngIf="getPrimaryAddress(customer)?.notes && getPrimaryAddress(customer)?.notes !== 'nan'" class="flex align-items-start gap-3">
                <i class="pi pi-info-circle text-500"></i>
                <span class="text-sm">{{getPrimaryAddress(customer)?.notes}}</span>
              </div>
            </div>
            
            <!-- Right Column: Contact Information -->
            <div class="flex-1 flex flex-column gap-3">
              <div *ngIf="getPrimaryAddress(customer) && hasServiceHours(getPrimaryAddress(customer)!)" class="flex align-items-start gap-3">
                <i class="pi pi-clock text-500"></i>
                <span class="text-sm">{{getServiceHours(getPrimaryAddress(customer)!)}}</span>
              </div>
              <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(getPrimaryAddress(customer)!)?.name">
                <i class="pi pi-user text-500"></i>
                <span class="font-medium">{{getPrimaryContact(getPrimaryAddress(customer)!)?.name}}</span>
              </div>
              <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(getPrimaryAddress(customer)!)?.email">
                <i class="pi pi-envelope text-500"></i>
                <span class="text-sm">{{getPrimaryContact(getPrimaryAddress(customer)!)?.email}}</span>
              </div>
              <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(getPrimaryAddress(customer)!)?.phone">
                <i class="pi pi-phone text-500"></i>
                <span class="text-sm">{{getPrimaryContact(getPrimaryAddress(customer)!)?.phone}}</span>
              </div>
            </div>
          </div>
          
          <!-- Fallback: Single address but multiple contacts - use two-column with accordion -->
          <div *ngIf="!getPrimaryAddress(customer) || getContactCount(getPrimaryAddress(customer)!) !== 1" class="flex gap-6">
            <!-- Left Column: Address Information and Notes -->
            <div class="flex-1 flex flex-column gap-3">
              <div class="flex align-items-start gap-3">
                <i class="pi pi-map-marker text-500 mt-1"></i>
                <div class="flex flex-column gap-1">
                  <span class="font-medium">{{getCustomerAddress(customer)}}</span>
                  <span class="text-sm text-500">{{getCustomerCity(customer)}}, {{getCustomerState(customer)}} {{getCustomerZipcode(customer)}}</span>
                </div>
              </div>
              <div *ngIf="getPrimaryAddress(customer)?.notes && getPrimaryAddress(customer)?.notes !== 'nan'" class="flex align-items-start gap-3">
                <i class="pi pi-info-circle text-500"></i>
                <span class="text-sm">{{getPrimaryAddress(customer)?.notes}}</span>
              </div>
              <div *ngIf="getPrimaryAddress(customer) && hasServiceHours(getPrimaryAddress(customer)!)" class="flex align-items-start gap-3">
                <i class="pi pi-clock text-500" pTooltip="Service Hours"></i>
                <div class="flex flex-column gap-1">
                  <!-- <span class="text-sm font-medium">Service Hours</span> -->
                  <span class="text-sm font-medium">{{getServiceHours(getPrimaryAddress(customer)!)}}</span>
                </div>
              </div>
            </div>
            
            <!-- Right Column: Contact Information with Accordion -->
            <div class="flex-1 flex flex-column gap-3">
              <div *ngIf="getPrimaryAddress(customer) && hasContacts(getPrimaryAddress(customer)!)">
                <p-accordion [activeIndex]="0">
                  <p-accordionTab *ngFor="let contact of getPrimaryAddress(customer)!.contacts; let i = index" 
                                  [header]="contact.name || 'Contact ' + (i + 1)">
                    <div class="flex flex-column gap-2">
                      <div class="flex align-items-center gap-3" *ngIf="contact.email">
                        <i class="pi pi-envelope text-500"></i>
                        <span class="text-sm">{{contact.email}}</span>
                      </div>
                      <div class="flex align-items-center gap-3" *ngIf="contact.phone">
                        <i class="pi pi-phone text-500"></i>
                        <span class="text-sm">{{contact.phone}}</span>
                      </div>
                    </div>
                  </p-accordionTab>
                </p-accordion>
              </div>
              <div *ngIf="!getPrimaryAddress(customer) || !hasContacts(getPrimaryAddress(customer)!)" class="flex align-items-center gap-3 text-500">
                <i class="pi pi-info-circle"></i>
                <span class="text-sm">No contacts available</span>
              </div>
            </div>
          </div>
        </div>


        
        <!-- Multiple Addresses with Tabs -->
        <div *ngIf="hasMultipleAddresses(customer)" class="w-full">
          <p-tabView styleClass="p-tabview-custom" [activeIndex]="getActiveTabIndex(customer.customer_id)">
            <p-tabPanel *ngFor="let address of customer.addresses; let i = index" 
                        [header]="address.city + ', ' + address.state">
              <ng-template pTemplate="header">
                <div class="flex align-items-center justify-content-between w-full">
                  <span>{{address.city}}, {{address.state}}</span>
                </div>
              </ng-template>
              
              <!-- Consistent two-column layout for all tabs -->
              <div class="flex gap-4 pt-2">
                <!-- Left Column: Address Information and Notes -->
                <div class="flex-1 flex flex-column gap-3">
                  <div class="flex align-items-start gap-3">
                    <i class="pi pi-map-marker text-500 mt-1"></i>
                    <div class="flex flex-column gap-1">
                      <span class="font-medium">{{address.address}}</span>
                      <span class="text-sm text-500">{{address.city}}, {{address.state}} {{address.zipcode}}</span>
                    </div>
                  </div>
                  <div *ngIf="address.notes && address.notes !== 'nan'" class="flex align-items-start gap-3">
                    <i class="pi pi-info-circle text-500"></i>
                    <span class="text-sm">{{address.notes}}</span>
                  </div>
                  <div *ngIf="hasServiceHours(address)" class="flex align-items-start gap-3">
                    <i class="pi pi-clock text-500 mt-1"></i>
                    <div class="flex flex-column gap-1">
                      <span class="text-sm font-medium">Service Hours</span>
                      <span class="text-sm text-500">{{getServiceHours(address)}}</span>
                    </div>
                  </div>
                </div>
                
                <!-- Right Column: Contact Information with Accordion -->
                <div class="flex-1 flex flex-column gap-3">
                  <div *ngIf="hasContacts(address) && getContactCount(address) > 1" >
                    <p-accordion [activeIndex]="0">
                      <p-accordionTab *ngFor="let contact of address.contacts; let i = index" 
                                      [header]="contact.name || 'Contact ' + (i + 1)">
                        <ng-template pTemplate="header">
                        </ng-template>
                        <div class="flex flex-column gap-2">
                          <div class="flex align-items-center gap-3" *ngIf="contact.email">
                            <i class="pi pi-envelope text-500"></i>
                            <span class="text-sm">{{contact.email}}</span>
                          </div>
                          <div class="flex align-items-center gap-3" *ngIf="contact.phone">
                            <i class="pi pi-phone text-500"></i>
                            <span class="text-sm">{{contact.phone}}</span>
                          </div>
                        </div>
                      </p-accordionTab>
                    </p-accordion>
                  </div>
                    <div *ngIf="hasContacts(address) && getContactCount(address) === 1" class="flex flex-column gap-3">
                     <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(address)!?.name">
                       <i class="pi pi-user text-500"></i>
                       <span class="text-sm">{{getPrimaryContact(address)!.name}}</span>
                     </div>
                     <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(address)!?.email">
                       <i class="pi pi-envelope text-500"></i>
                       <span class="text-sm">{{getPrimaryContact(address)!.email}}</span>
                     </div>
                     <div class="flex align-items-center gap-3" *ngIf="getPrimaryContact(address)!?.phone">
                       <i class="pi pi-phone text-500"></i>
                       <span class="text-sm">{{getPrimaryContact(address)!.phone}}</span>
                     </div>
                   </div>
                  
                  <div *ngIf="!hasContacts(address)" class="flex align-items-center gap-3 text-500">
                    <i class="pi pi-info-circle"></i>
                    <span class="text-sm">No contacts available</span>
                  </div>
                </div>
              </div>
              
            </p-tabPanel>
          </p-tabView>
        </div>
      </p-card>
    </div>
  </div>

  <!-- Address Selection Dialog -->
  <p-dialog [(visible)]="displayAddressSelectionDialog" header="Select Address to Edit" [modal]="true" [style]="{width: '500px'}"
    styleClass="address-selection-dialog" [draggable]="false">
    <div class="flex flex-column gap-3">
      <p class="text-sm text-600 mb-3">This customer has multiple addresses. Please select which address you want to edit:</p>
      
      <div class="flex flex-column gap-2">
        <!-- Existing Addresses -->
        <div *ngFor="let address of customerToEdit?.addresses" 
             class="p-3 border-1 border-200 border-round cursor-pointer hover:bg-surface-50 transition-colors"
             (click)="selectAddressToEdit(address)">
          <div class="flex justify-content-between align-items-start">
            <div class="flex-1">
              <div class="font-medium text-lg">{{address.address}}</div>
              <div class="text-sm text-600 mt-1">{{address.city}}, {{address.state}}</div>
            </div>
            <div class="flex flex-column align-items-end gap-1">
              <div class="flex align-items-center gap-2">
                <p-tag *ngIf="hasServiceHours(address)" value="Has Service Hours" severity="info" styleClass="text-xs"></p-tag>
                <p-tag *ngIf="hasContacts(address)" [value]="getContactCount(address) + ' Contacts'" severity="success" styleClass="text-xs"></p-tag>
                <button *ngIf="hasAccess && customerToEdit && customerToEdit.addresses && customerToEdit.addresses.length > 1" 
                        pButton 
                        type="button" 
                        icon="pi pi-trash" 
                        (click)="confirmDeleteAddress(customerToEdit, address); $event.stopPropagation()"
                        class="p-button-rounded p-button-text p-button-sm p-button-danger"
                        [style]="{'width': '24px', 'height': '24px'}"
                        pTooltip="Delete Address"
                        tooltipPosition="top">
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Add New Address Option -->
        <div class="p-3 border-1 border-300 border-round cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-all border-dashed"
             (click)="addNewAddressToCustomer()">
          <div class="flex align-items-center justify-content-center gap-3 text-primary">
            <i class="pi pi-plus-circle text-2xl"></i>
            <div class="flex flex-column align-items-center">
              <span class="font-medium text-lg">Add New Address</span>
              <span class="text-sm">Create additional location for this customer</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button pButton label="Cancel" (click)="displayAddressSelectionDialog=false" class="p-button-outlined"></button>
    </ng-template>
  </p-dialog>

  <app-customer-form 
    [(displayAddDialog)]="displayAddDialog"
    [availableCustomers]="availableCustomers"
    (customerSaved)="onCustomerSaved()">
  </app-customer-form>

  <!-- Edit Customer Dialog -->
  <p-dialog [(visible)]="displayEditDialog" header="Edit Customer" [modal]="true" [style]="{width: '800px'}"
    styleClass="customer-dialog" [draggable]="false">
    <ng-template pTemplate="header">
      <div class="flex justify-content-between align-items-center w-full">
        <div class="flex flex-column gap-1">
          <span class="text-xl font-bold">Edit Customer</span>
          <span *ngIf="selectedCustomer.selectedAddressId === null" class="text-sm text-primary font-medium">
            <i class="pi pi-plus mr-1"></i>Adding New Address
          </span>
          <span *ngIf="selectedCustomer.selectedAddressId !== null" class="text-sm text-600">
          </span>
        </div>
        <div class="flex align-items-center gap-2">
          <p-dropdown [options]="customerTypeOptions" [(ngModel)]="selectedCustomer.customer_type" [style]="{'width': '140px'}"
            placeholder="Select Type" optionLabel="label" optionValue="value"></p-dropdown>
          <p-dropdown [options]="statusOptions" [(ngModel)]="selectedCustomer.status" [style]="{'width': '120px'}"
            placeholder="Select Status" optionLabel="label" optionValue="value"></p-dropdown>
        </div>
      </div>
    </ng-template>
    <div class="flex flex-column gap-3">
      <!-- Top Row: Customer Details and Service Hours -->
      <div class="flex gap-3">
        <!-- Customer Details -->
        <div class="flex-1 surface-section p-3 border-round-lg shadow-1" 
             style="background-color: var(--surface-card);">
          <div class="text-lg font-semibold text-900 mb-5">
            <i class="pi pi-user mr-2"></i>
            Customer Details
          </div>
          <span class="p-float-label w-full">
            <input pInputText id="edit-name" [(ngModel)]="selectedCustomer.name" class="w-full" />
            <label for="edit-name">Customer Name</label>
          </span>
        </div>

        <!-- Service Hours -->
        <div class="flex-1 surface-section p-3 border-round-lg shadow-1" 
             style="background-color: var(--surface-card);">
          <div class="text-lg font-semibold text-900 mb-3">
            <i class="pi pi-clock mr-2"></i>
            Service Hours
          </div>
          <div class="flex gap-2">
            <div class="flex-1">
              <label for="edit_service_hours_start" class="block text-sm font-medium mb-2">Start</label>
              <input type="time" pInputText id="edit_service_hours_start" [(ngModel)]="selectedCustomer.service_hours_start" class="w-full" />
            </div>
            <div class="flex-1">
              <label for="edit_service_hours_end" class="block text-sm font-medium mb-2">End</label>
              <input type="time" pInputText id="edit_service_hours_end" [(ngModel)]="selectedCustomer.service_hours_end" class="w-full" />
            </div>
          </div>
        </div>
      </div>

      <!-- Address Information -->
      <div class="surface-section p-3 border-round-lg shadow-1" 
           style="background-color: var(--surface-card);">
        <div class="text-lg font-semibold text-900 mb-5">
          <i class="pi pi-map-marker mr-2"></i>
          Address Information
        </div>
        <div class="flex flex-column gap-3">
          <span class="p-float-label w-full">
            <textarea pInputTextarea id="edit-address" [(ngModel)]="selectedCustomer.address" [rows]="1"
              class="w-full"></textarea>
            <label for="edit-address">Street Address</label>
          </span>
          <div class="flex gap-3">
            <span class="p-float-label flex-1">
              <input pInputText id="edit-city" [(ngModel)]="selectedCustomer.city" class="w-full" />
              <label for="edit-city">City</label>
            </span>
            <span class="p-float-label" style="width: 100px;">
              <input pInputText id="edit-state" [(ngModel)]="selectedCustomer.state" class="w-full" />
              <label for="edit-state">State</label>
            </span>
            <span class="p-float-label" style="width: 120px;">
              <input pInputText id="edit-zip" [(ngModel)]="selectedCustomer.zipcode" class="w-full" />
              <label for="edit-zip">ZIP Code</label>
            </span>
          </div>
          <span class="p-float-label w-full">
            <textarea pInputTextarea id="edit-notes" [(ngModel)]="selectedCustomer.notes" [rows]="1"
              class="w-full"></textarea>
            <label for="edit-notes">Address Notes</label>
          </span>
        </div>
        <div class="flex justify-content-center pt-2">
          <button pButton 
                  label="Add New Location" 
                  icon="pi pi-plus" 
                  (click)="addNewAddressToCustomer()" 
                  class="p-button-outlined p-button-sm"></button>
        </div>
      </div>

      <!-- Customer Relationships Section for Edit -->
      <div *ngIf="selectedCustomer.customer_type === 'Supplier' || selectedCustomer.customer_type === 'Receiver' || selectedCustomer.customer_type === 'Both'" 
           class="surface-section p-3 border-round-lg shadow-1" 
           style="background-color: var(--surface-card);">
        <div class="text-lg font-semibold text-900 mb-5">
          <i class="pi pi-link mr-2"></i>
          Customer Relationships
        </div>
        <div class="flex gap-3">
          <!-- Supplies To (Delivers To) -->
          <div *ngIf="selectedCustomer.customer_type === 'Supplier' || selectedCustomer.customer_type === 'Both'" class="flex-1">
            <span class="p-float-label w-full">
              <p-multiSelect 
                id="edit_supplies_to" 
                [options]="availableReceiversForRelationship"
                [(ngModel)]="selectedCustomer.delivers_to"
                optionLabel="name"
                optionValue="customer_id"
                placeholder="Select customers this supplier delivers to"
                [filter]="true"
                filterBy="name"
                [showToggleAll]="false"
                class="w-full">
              </p-multiSelect>
              <label for="edit_supplies_to">Supplies To</label>
            </span>
          </div>
          
          <!-- Receives From -->
          <div *ngIf="selectedCustomer.customer_type === 'Receiver' || selectedCustomer.customer_type === 'Both'" class="flex-1">
            <span class="p-float-label w-full">
              <p-multiSelect 
                id="edit_receives_from" 
                [options]="availableSuppliersForRelationship"
                [(ngModel)]="selectedCustomer.receives_from"
                optionLabel="name"
                optionValue="customer_id"
                placeholder="Select customers this receiver gets deliveries from"
                [filter]="true"
                filterBy="name"
                [showToggleAll]="false"
                class="w-full">
              </p-multiSelect>
              <label for="edit_receives_from">Receives From</label>
            </span>
          </div>
        </div>
      </div>

      <!-- Contacts Section -->
      <app-contact-management 
        [selectedCustomer]="selectedCustomer"
        [isAdmin]="isAdmin"
        [hasAccess]="hasAccess"
        [customers]="customers"
        (contactUpdated)="onContactUpdated()"
        (contactDeleted)="onContactDeleted()">
      </app-contact-management>
    </div>

    <ng-template pTemplate="footer">
      <div class="flex justify-content-between gap-2">
        <button *ngIf="isAdmin" pButton label="Delete Customer" icon="pi pi-trash" class="p-button-danger"
          (click)="deleteCustomer(selectedCustomer.customer_id)"></button>
        <div class="flex gap-2">
          <button pButton label="Cancel" (click)="displayEditDialog=false" class="p-button-outlined"></button>
          <button pButton 
                  [label]="selectedCustomer.selectedAddressId === null ? 'Add New Address' : 'Save Changes'" 
                  [icon]="selectedCustomer.selectedAddressId === null ? 'pi pi-plus' : 'pi pi-check'" 
                  (click)="saveEditedCustomer()"
                  [disabled]="savingEditedCustomer"
                  [loading]="savingEditedCustomer">
          </button>
        </div>
      </div>
    </ng-template>
  </p-dialog>

  <!-- Add Address Dialog using Address Management Component -->
  <app-address-management 
    [displayAddAddressDialog]="displayAddAddressDialog"
    [selectedCustomer]="selectedCustomer"
    [customers]="customers"
    [locations]="locations"
    (displayAddAddressDialogChange)="displayAddAddressDialog = $event"
    (addressSaved)="onAddressSaved()">
  </app-address-management>
</div>

<p-confirmDialog></p-confirmDialog>